import { useState } from "react";
import { motion } from "framer-motion";
import { Mail, ArrowRight, LoaderCircle } from "lucide-react";
import { toast } from "sonner";
import axios from "axios";
import { COMMUNITY_BENEFITS } from "@/data/content";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const CommunitySection = ({ onOpenLegal }) => {
    const [email, setEmail] = useState("");
    const [loading, setLoading] = useState(false);

    const submit = async (e) => {
        e.preventDefault();
        const trimmed = email.trim();
        if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(trimmed)) {
            toast.error("Please enter a valid email address.");
            return;
        }
        setLoading(true);
        try {
            const { data } = await axios.post(`${API}/subscribe`, { email: trimmed });
            if (data.status === "already_subscribed") {
                toast.info("You're already part of the family.");
            } else {
                toast.success("Welcome to the ISelfLawAmMaster family. Check your inbox for your welcome email.");
            }
            setEmail("");
        } catch (err) {
            toast.error("Something went wrong. Please try again.");
        } finally {
            setLoading(false);
        }
    };

    return (
        <section id="community" data-testid="community-section" className="relative overflow-hidden py-24 sm:py-28">
            <div className="pointer-events-none absolute inset-0" aria-hidden="true">
                <div className="absolute left-1/2 top-1/2 h-[420px] w-[720px] -translate-x-1/2 -translate-y-1/2 rounded-full bg-[#7200C8]/18 blur-[150px]" />
            </div>

            <div className="relative mx-auto max-w-4xl px-5 lg:px-8">
                <motion.div
                    initial={{ opacity: 0, y: 34 }}
                    whileInView={{ opacity: 1, y: 0 }}
                    viewport={{ once: true, margin: "-80px" }}
                    transition={{ duration: 0.8, ease: [0.22, 1, 0.36, 1] }}
                    className="relative rounded-3xl border border-[#D9A441]/35 bg-[#0B0E18]/80 p-8 shadow-[0_0_80px_rgba(217,164,65,0.12)] backdrop-blur-xl sm:p-12"
                >
                    <div className="pointer-events-none absolute inset-0 rounded-3xl ring-1 ring-inset ring-[#F4C46B]/15" aria-hidden="true" />

                    <div className="text-center">
                        <p className="text-xs font-semibold uppercase tracking-[0.3em] text-[#D9A441]">Real Knowledge. Real Solutions. Real Impact.</p>
                        <h2 data-testid="community-heading" className="mt-5 font-display text-3xl font-bold tracking-tight text-[#F8F5EE] sm:text-4xl">
                            Join Our <span className="text-gold-gradient">Community</span>
                        </h2>
                        <p className="mx-auto mt-4 max-w-xl text-sm leading-relaxed text-[#F8F5EE]/60 sm:text-base">
                            Get educational insights, important announcements, exclusive resources, and updates delivered directly to your inbox.
                        </p>
                    </div>

                    <form data-testid="newsletter-form" onSubmit={submit} className="mx-auto mt-9 flex max-w-xl flex-col gap-3 sm:flex-row" noValidate>
                        <label htmlFor="newsletter-email" className="sr-only">Email address</label>
                        <div className="relative flex-1">
                            <Mail className="pointer-events-none absolute left-4 top-1/2 h-4 w-4 -translate-y-1/2 text-[#D9A441]/70" />
                            <input
                                id="newsletter-email"
                                data-testid="newsletter-email-input"
                                type="email"
                                value={email}
                                onChange={(e) => setEmail(e.target.value)}
                                placeholder="Enter your email address"
                                className="w-full rounded-full border border-[#D9A441]/30 bg-[#080B12] py-3.5 pl-11 pr-5 text-sm text-[#F8F5EE] placeholder:text-[#F8F5EE]/35 transition-colors duration-300 focus:border-[#D9A441] focus:outline-none"
                                autoComplete="email"
                            />
                        </div>
                        <button
                            data-testid="newsletter-submit-button"
                            type="submit"
                            disabled={loading}
                            className="btn-shine flex items-center justify-center gap-2 rounded-full bg-gold-gradient px-8 py-3.5 text-sm font-bold uppercase tracking-[0.12em] text-[#080B12] transition-transform duration-300 hover:scale-[1.04] disabled:opacity-60"
                        >
                            {loading ? <LoaderCircle className="h-4 w-4 animate-spin" /> : <ArrowRight className="h-4 w-4" />}
                            {loading ? "Joining" : "Join Now"}
                        </button>
                    </form>

                    <p className="mt-4 text-center text-xs text-[#F8F5EE]/40">
                        By joining, you consent to receive educational emails from ISelfLawAmMaster. Read our{" "}
                        <button
                            type="button"
                            data-testid="newsletter-privacy-link"
                            onClick={() => onOpenLegal("privacy")}
                            className="text-[#D9A441] underline underline-offset-2 transition-colors hover:text-[#F4C46B]"
                        >
                            Privacy Policy
                        </button>
                        .
                    </p>

                    <div className="mt-10 grid grid-cols-2 gap-4 border-t border-[#D9A441]/15 pt-8 sm:grid-cols-4">
                        {COMMUNITY_BENEFITS.map((item, i) => (
                            <motion.div
                                key={item.label}
                                data-testid={`community-benefit-${i}`}
                                initial={{ opacity: 0, y: 16 }}
                                whileInView={{ opacity: 1, y: 0 }}
                                viewport={{ once: true }}
                                transition={{ delay: 0.15 + i * 0.08 }}
                                className="flex flex-col items-center gap-2.5 text-center"
                            >
                                <item.icon className="h-5 w-5 text-[#D9A441]" strokeWidth={1.7} />
                                <span className="text-[11px] font-semibold uppercase tracking-[0.14em] text-[#F8F5EE]/65">{item.label}</span>
                            </motion.div>
                        ))}
                    </div>
                </motion.div>
            </div>
        </section>
    );
};

export default CommunitySection;
